import React, { useState, useContext, useEffect  } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import Env from "../../runtimeEnv";
import TherapistSidebar from "../../components/TherapistSidebar";
import { AuthContext } from "../../context/AuthContext";

const TherapistCreateMeetingForm = () => {
  const location = useLocation();
const selectedClientId = location.state?.clientId;
const selectedClientEmail = location.state?.clientEmail;
  const { register, handleSubmit, setValue, getFieldState } = useForm();

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeNavItem, setActiveNavItem] = useState("create-meeting");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // 🔵 Get therapist email from AuthContext
  const { therapistEmail } = useContext(AuthContext);

  useEffect(() => {
  if (selectedClientEmail) {
    setValue("email", selectedClientEmail);
  }
}, [selectedClientEmail, setValue]);


  useEffect(() => {
    const controller = new AbortController();
    axios.get(`${Env.REACT_APP_PYTHON_API_URL}/api/therapist/schedule`,
      { withCredentials: true, signal: controller.signal })
      .then(({ data }) => {
        const clientName = location.state?.clientName;
        if (data.therapistName && clientName && !getFieldState("meetingName").isDirty) {
          setValue("meetingName", `${data.therapistName} with ${clientName}`, { shouldDirty: false });
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, [location.state?.clientName, setValue, getFieldState]);

  // 🔵 Autofill therapistEmail in the form on mount
  useEffect(() => {
    if (therapistEmail) {
      setValue("therapistEmail", therapistEmail);
    }
  }, [therapistEmail, setValue]);

  // 🔵 Handle form submit
  const onSubmit = async (formData) => {
    setLoading(true);
    setMessage(null);

    try {
      if (!therapistEmail) {
        throw new Error("Your therapist email is unavailable. Please sign out and sign in again.");
      }

      if (!formData.meetingName) {
        formData.meetingName = "Therapy Session";
      }

      formData.email = selectedClientEmail;

      // remove unnecessary values
      delete formData.therapist;

      const response = await axios.post(
        `${Env.REACT_APP_PYTHON_API_URL}/api/therapist/schedule`,
        formData,
        { withCredentials: true }
      );

      setMessage({ type: "success", text: "Session scheduled successfully!" });

      setValue("therapistEmail", therapistEmail); // keep therapist email after reset
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || error.message || "Failed to schedule session.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex bg-gray-50">
      {/* Sidebar */}
      <TherapistSidebar
        activeNavItem={activeNavItem}
        setActiveNavItem={setActiveNavItem}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
      />

      {/* Main Content */}
      <div
        className={`flex-1 px-4 py-8 sm:px-6 lg:px-8 transition-all ${
          sidebarCollapsed ? "ml-8" : "ml-8"
        }`}
      >
        <div className="max-w-4xl mx-auto">
          <Link to="/clients" className="text-blue-600 hover:underline">← Back to clients</Link>

          {message && (
            <div
              className={`p-3 text-center rounded-md mb-6 ${
                message.type === "success"
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}
            >
              {message.text}
            </div>
          )}

          <div className="space-y-5">

            {/* Client Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Email
              </label>
              <input
                {...register("email")}
                readOnly
                placeholder="Client Email"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Meeting Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meeting Name
              </label>
              <input
                {...register("meetingName")}
                placeholder="Meeting Name"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Date & Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session Date & Time
              </label>
              <input
                type="datetime-local"
                {...register("sessionTime")}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Therapist Email (Autofilled) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Therapist Email
              </label>
              <input
                {...register("therapistEmail")}
                value={therapistEmail || ""}
                readOnly
                className="w-full px-4 py-3 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
              />
            </div>

            <button
              type="submit"
              onClick={handleSubmit(onSubmit)}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm transition"
              disabled={loading || !selectedClientEmail}
            >
              {loading ? "Scheduling..." : "Schedule Session"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TherapistCreateMeetingForm;
